import { Activity } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "./ui/button";

interface AuthGateProps {
  status: "loading" | "signed-out" | "error";
  errorMessage?: string;
  onSignIn: () => void;
  onRetry?: () => void;
}

const TEASER_POINTS = [
  "Market-wide Form 4 screener with explainable scores",
  "Today feed + action queue for high-signal buys",
  "Watchlists, saved views, and email alert rules"
] as const;

function AuthGateThemeToggle() {
  return (
    <div className="fixed right-4 top-4 z-10">
      <ThemeToggle />
    </div>
  );
}

export function AuthGate({ status, errorMessage, onSignIn, onRetry }: AuthGateProps) {
  if (status === "loading") {
    return (
      <div className="landing-shell flex min-h-screen items-center justify-center bg-background px-4">
        <AuthGateThemeToggle />
        <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-7 text-center shadow-[0_24px_80px_rgb(0_0_0/0.28)]">
          <h1 className="text-sm font-semibold">Checking session…</h1>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            TrackInsiders requires sign-in before the screener loads.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="landing-shell flex min-h-screen items-center justify-center bg-background px-4">
        <AuthGateThemeToggle />
        <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-7 text-center shadow-[0_24px_80px_rgb(0_0_0/0.28)]">
          <h1 className="text-sm font-semibold">Could not verify sign-in</h1>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            {errorMessage || "Session lookup failed. Retry or sign in again."}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            {onRetry ? (
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            ) : null}
            <Button type="button" size="sm" onClick={onSignIn}>
              Sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-shell relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <AuthGateThemeToggle />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgb(var(--accent-rgb)/0.16),transparent_45%),radial-gradient(ellipse_at_bottom,rgb(var(--buy-rgb)/0.08),transparent_40%)]"
      />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-surface/95 p-7 shadow-[0_24px_80px_rgb(0_0_0/0.32)] backdrop-blur">
        <div className="text-center">
          <span className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-accent/40 bg-accent-soft text-accent">
            <Activity className="h-4 w-4" />
          </span>
          <h1 className="text-sm font-semibold">Sign in to use TrackInsiders</h1>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            The insider screener, watchlists, and alerts are available after email sign-in. No
            Cloudflare Access seat required.
          </p>
        </div>
        <ul className="mt-5 space-y-2 rounded-xl border border-border bg-surface-inset p-4 text-left text-xs text-muted-foreground">
          {TEASER_POINTS.map((point) => (
            <li key={point} className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex justify-center">
          <Button type="button" size="sm" className="rounded-full px-5" onClick={onSignIn}>
            Sign in with email
          </Button>
        </div>
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          We email a one-time link (usually under a minute). Your screener URL is restored after
          sign-in.
        </p>
      </div>
    </div>
  );
}
